import { StyledProps } from '@front10';
import useStyles from '@front10/helpers/dist/UseStyles';
import { isSSR } from '@front10/utils';
import React, { FunctionComponent } from 'react';
//-------------------------------------
// Integration Layer Default Actions
//-------------------------------------
import { getAirports as getAirportsFunction } from '../../../_integrations/everymundo/airports';
import { getFares as getFaresFunction } from '../../../_integrations/everymundo/fares';
import { getNames } from '../../../_integrations/everymundo/map';
//-------------------------------------
// Atoms
//-------------------------------------
import InlineFilters from '../../controls/InlineFilters/ReducedInlineFilterMobile';
import Box from '../../elements/Box/Box';
import Col from '../../elements/Col/Col';
import Row from '../../elements/Row/Row';
import SecondaryFilters from '../../controls/SecondaryFilters/ReducedSecondaryFilter';
import Disclaimer from '../../others/Disclaimer';
import Skeleton from '../../others/Skeleton/SkeletonInteractiveMapMobile';
import InteractiveMap from '../InteractiveMapContainer/InteractiveMap';
import TravelRestrictionContainer from '../TravelRestrictionContainer';
//-------------------------------------
// PROPS DEFINITION
//-------------------------------------
import type {
  Components,
  MainProps,
  TranslationLabels,
  UIDisplay
} from '../InteractiveMapContainer/InteractiveMapContainer';
//-------------------------------------
// HOOKS
//-------------------------------------
import useFaresApi from '../InteractiveMapContainer/filters/useFares';
import useFilters from '../InteractiveMapContainer/filters/useFilters';
import useFiltersDisableSettings from '../InteractiveMapContainer/filters/useFiltersDisabled';
import useSyncFiltersFaresApi from '../InteractiveMapContainer/filters/useSyncFiltersFareApi';
import useCountriesGeoData from '../InteractiveMapContainer/hooks/useCountriesData';
import useNameGeoData from '../InteractiveMapContainer/hooks/useNameGeoData';
import withCommonEMContainer from '../_commons/CommonEMContainer/CommonEMContainer';
import prePopulationDefaultProps from '../_commons/prepopulationDefaultProps/groupedRoutesAggregation';
import HeaderContainer from '../../elements/HeaderContainer';
import {
  useStore,
  visibleDestinationsSelector,
  getZoomSelector
} from '../InteractiveMapContainer/hooks/useIMCState';
import { withQueryClient } from '@front10/container-elements';

export type InteractiveMapContainerProps = StyledProps<
  MainProps & Partial<Components> & Partial<UIDisplay> & TranslationLabels
>;

const InteractiveMapContainerMobile: FunctionComponent<InteractiveMapContainerProps> = (
  props: InteractiveMapContainerProps
) => {
  const {
    faresTransformationLogic = 'grouped-routes',
    filters = {
      availableFilters: ['OriginDestination'],
      originDestinationSettings: {
        showOrigin: true,
        showDestination: true
      }
    },
    secondaryFilters = {},
    disclaimerSettings = {},
    skeletonSettings = {
      style: { backgroundColor: 'white' }
    },
    mainContentSettings,
    showFilters = true,
    showSecondaryFilters = true,
    showSkeletonScreen = true,
    showDisclaimer = true,
    initialFares,
    integrationSettings,
    meta,
    pageContext,
    getFares,
    getAirports,
    prePopulation,
    translate,
    direction,
    theme,
    hideBudgetOnMultiCurrency,
    budgetSource,
    disclaimerContentSettings,
    vectorJSONUrl = 'https://em-frontend-assets.airtrfx.com/assets/map-tiles/world-countries-usa-states.json',
    vectorJSONUrlAxiosWrapperOptions = {},
    showTravelRestriction = false,
    enableNameService = false,
    travelRestrictionSettings = {},
    paramsTrackingExtra,
    enableSEO = true,
    ...interactiveMapProps
  } = props;
  const [className, style] = useStyles(props, { direction, theme });
  const myStyle = Object.assign({ minHeight: 630 }, style);

  const geoData = useCountriesGeoData(vectorJSONUrl, vectorJSONUrlAxiosWrapperOptions);
  const namesData = useNameGeoData(
    getNames,
    { ...prePopulation, enableNameService, zoom: useStore(getZoomSelector) },
    integrationSettings,
    meta,
    pageContext
  );

  const filtersState = useFilters({
    filter: { prePopulation, integrationSettings, meta, pageContext, getAirports },
    availableFilters: showFilters ? filters.availableFilters : [],
    filterSettings: { ...filters, ...secondaryFilters },
    availableSecondaryFilters: showSecondaryFilters ? secondaryFilters.availableFilters : [],
    hideBudgetOnMultiCurrency,
    showInitialData:
      props.vectorMapSettings?.showInitialData === undefined ||
      props.vectorMapSettings?.showInitialData === null
        ? true
        : props.vectorMapSettings?.showInitialData
  });

  const origin = filtersState.locations.origin.value;
  const visibleDestinations = useStore(visibleDestinationsSelector);

  const fares = useFaresApi({
    getFares,
    prePopulation,
    integrationSettings,
    faresTransformationLogic,
    budgetSource,
    meta,
    pageContext,
    paginationSettings: props.paginationSettings,
    filters: {
      origins: origin ? [origin] : undefined,
      destinations: visibleDestinations === null ? [] : visibleDestinations,
      budget: filtersState.budget.value,
      dateRange: filtersState.dateRange.value,
      flightType: filtersState.flightType.value,
      sorting: filtersState.sorting.value,
      travelClasses: filtersState.travelClass.value,
      flightStopCount: filtersState.flightStopCount.value,
      travelerInterest: filtersState.travelerInterest?.selectedItems,
      paramsTrackingExtra
    }
  });

  const extendedFiltersModel = useSyncFiltersFaresApi(
    filtersState,
    {
      clearAllOnOriginChange: true
    },
    prePopulation
  );

  const isOriginSelected = Boolean(origin);

  const secondaryFiltersDisableSettings = useFiltersDisableSettings(secondaryFilters, {
    dateRange: !isOriginSelected,
    budget: !isOriginSelected,
    flightType: !isOriginSelected,
    travelClass: !isOriginSelected,
    travelerInterest: !isOriginSelected,
    flightStopCount: !isOriginSelected
  });

  if (isSSR() && !Boolean(enableSEO)) {
    return null;
  }

  const filtersConfig = {
    originDestinationSettings: {
      ...filters.originDestinationSettings
    },
    locations: {
      ...extendedFiltersModel.locations
    }
  };

  const renderFilters = showFilters && (
    <InlineFilters
      dataTestId="interactiveMapContainerMobile"
      {...filters}
      {...filtersState}
      {...extendedFiltersModel}
    />
  );

  const renderSecondaryFilters = showSecondaryFilters && (
    <SecondaryFilters
      {...filtersConfig}
      {...filtersState}
      {...secondaryFilters}
      {...secondaryFiltersDisableSettings}
      availableFilters={filtersState.availableSecondaryFilters}
      filter={{ ...{ prePopulation, integrationSettings, meta, getAirports } }}
    />
  );

  const renderDisclaimer = showDisclaimer && (
    <Row {...disclaimerContentSettings}>
      <Col>
        <Disclaimer labelKey="cmp-interactive-map-container-disclaimer" {...disclaimerSettings} />
      </Col>
    </Row>
  );

  const isSkeletonShow =
    showSkeletonScreen && (geoData.isLoading || filtersState.locations.origin.isLoading);

  function handleSeeMoreDeals() {
    filtersState.tracking({ type: 'MORE_DEALS' });
    return true;
  }

  return (
    <>
      <Box
        position="relative"
        className={`interactive-map-container ${className}`}
        style={myStyle}
        dataTestId="InteractiveMapContainer"
      >
        {isSkeletonShow && <Skeleton {...skeletonSettings} position="absolute" width="100" />}
        {!isSkeletonShow && (
          <>
            <HeaderContainer
              defaultTitleValue={'INTERACTIVE MAP MODULE'}
              handleSeeMoreDeals={handleSeeMoreDeals}
              {...(props as any)}
            />
            <Row gutters {...mainContentSettings}>
              <InteractiveMap
                renderFilters={renderFilters}
                renderSecondaryFilters={renderSecondaryFilters}
                filters={{ ...filtersState, ...extendedFiltersModel }}
                fares={fares}
                geoData={geoData}
                namesData={namesData}
                isMobile={true}
                dataForMarkerAction={{
                  getFares,
                  prePopulation,
                  integrationSettings,
                  faresTransformationLogic,
                  meta,
                  pageContext,
                  cache: true,
                  paginationSettings: props.paginationSettings,
                  filters: {
                    origins: origin ? [origin] : undefined,
                    destinations: visibleDestinations === null ? [] : visibleDestinations,
                    budget: filtersState.budget.value,
                    dateRange: filtersState.dateRange.value,
                    flightType: filtersState.flightType.value,
                    sorting: filtersState.sorting.value,
                    travelClasses: filtersState.travelClass.value,
                    flightStopCount: filtersState.flightStopCount.value
                  }
                }}
                {...interactiveMapProps}
              />
            </Row>
            {renderDisclaimer}
          </>
        )}
      </Box>
      {showTravelRestriction && (
        <TravelRestrictionContainer {...props} {...travelRestrictionSettings} />
      )}
    </>
  );
};

InteractiveMapContainerMobile.defaultProps = {
  titleContainerSettings: {},
  subHeaderContentSettings: {},
  showTitle: true,
  showSubTitle: false,
  titleKey: '',
  subTitleKey: '',
  titleSettings: {
    margin: '0',
    textAlign: 'left'
  },
  subTitleSettings: {
    margin: '0',
    textAlign: 'left'
  },
  getFares: getFaresFunction,
  getAirports: getAirportsFunction,
  prePopulation: prePopulationDefaultProps,
  tracking: {
    flexibleDates: false,
    passengerCount: 1,
    promoCode: 'EVERYMUNDO',
    eventType: 'InteractiveMapModule'
  },
  meta: null,
  integrationSettings: {},
  fareItem: {}
};

InteractiveMapContainerMobile.displayName = 'InteractiveMapContainerMobile';

export default withCommonEMContainer(withQueryClient(InteractiveMapContainerMobile));
